import { inject, observer } from 'mobx-react';
import React from 'react';
import { Form, Nav } from 'react-bootstrap';
import {
  Switch,
  Route,
  Redirect,
  NavLink,
  RouteComponentProps,
  withRouter,
} from 'react-router-dom';
import ModelBaseNames from '../../../models/modelBase/ModelBaseNames';
import { fetchTreeInsightsEnabled } from '../../components/model/rgipChatApi';
import Store from '../../../store';
import SmoothingPage from './SmoothingPage';
import ScoringRulesPage from './ScoringRulesPage';
import RulesPage from './RulesPage';
import ModelBaseChoicePage from '../ModelBaseChoicePage';
import ModelBasePage from './ModelBasePage';
import EnsemblingPage from './EnsemblingPage';
import OverridesPage from './OverridesPage';
import SignalsPage from './SignalsPage';
import TreesPage from './TreesPage';
import NodeLeadsPage from './NodeLeadsPage';
import NodeUnivariatePage from './NodeUnivariatePage';
import BehavioralEnsemblingPage from './BehavioralEnsemblingPage';
import LtbSignalsPage from './LtbSignalsPage';
import LeadGradePage from './LeadGradePage';
import LeadGradeRevampPage from './LeadGradeRevampPage';
import FeaturesPage from './FeaturesPage';
import EventWeightsPage from './EventWeightsPage';
import FineTuningPage from './FineTuningPage';
import EngagementOverridesPage from './EngagementOverridesPage';

type MatchParams = {
  tenant: string;
  modelId: string;
};

interface ModelDetailsPageProps extends RouteComponentProps<MatchParams> {
  store?: Store;
}

type ModelDetailsPageState = {
  isAdvancedMode: boolean;
  // null while the flag is resolving, so neither shape of the nav renders and
  // the tabs cannot flash from one to the other.
  isRevampEnabled: boolean | null;
};

const ModelDetailsPage = inject('store')(
  observer(
    class ModelDetailsPage extends React.Component<
      ModelDetailsPageProps,
      ModelDetailsPageState
    > {
      constructor(props: ModelDetailsPageProps) {
        super(props);

        this.state = {
          isAdvancedMode: false,
          isRevampEnabled: null,
        };
        this.toggleAdvancedMode = this.toggleAdvancedMode.bind(this);
      }

      async componentDidMount() {
        // studio-tree-insights, the same master switch the tree page redesign
        // uses, so a tenant gets the whole revamp or none of it. Deliberately NOT
        // studio-ai-model-insights: nothing on this page involves a model, and
        // hiding statistics behind a flag with `ai` in its name means whoever
        // operates it later turns them off without meaning to. Cached per tab and
        // fail-closed, so an LD outage leaves today's tabs in place.
        const enabled = await fetchTreeInsightsEnabled(
          this.props.match.params.tenant
        ).catch(() => false);
        this.setState({ isRevampEnabled: enabled });
      }

      toggleAdvancedMode() {
        this.setState({
          isAdvancedMode: !this.state.isAdvancedMode,
        });
      }

      render() {
        const { path, params } = this.props.match;
        const { tenant, modelId } = params;

        const basePath = `/tenant/${tenant}/models/${modelId}/model`;
        const { isAdvancedMode } = this.state;
        const { activeModel } = this.props.store;
        const { base: modelBase, type: modelType } = activeModel;

        const isCustomerFitModel = modelType === 'customer_fit';
        const isTreeBasedModel = modelBase === ModelBaseNames.trees;
        const { isRevampEnabled } = this.state;
        if (isRevampEnabled === null) return null; // flag still resolving
        // Thresholds and Overrides become one page, for customer fit only. Every
        // other model type keeps the tabs it has today.
        const isMerged = isRevampEnabled && isCustomerFitModel;
        // The engagement types take the revamp one page at a time, under the
        // same flag, so a tenant gets the whole revamp or none of it. Event
        // weights first: it carries the lift the dropped Insights tab used to
        // hold, which the revamp had left these models with no way to reach.
        const isEngagementRevamp = isRevampEnabled && !isCustomerFitModel;

        const getDefaultRoute = () => {
          if (!isCustomerFitModel) {
            return `${basePath}/event_weights`;
          }
          if (isAdvancedMode) {
            return `${basePath}/base`;
          }
          if (isTreeBasedModel) {
            return `${basePath}/trees`;
          }
          return `${basePath}/rules`;
        };

        return (
          <>
            <Nav className="third-level-nav model-details bg-white">
              {isCustomerFitModel ? (
                <>
                  {isAdvancedMode && (
                    <Nav.Link
                      className="text-dark"
                      as={NavLink}
                      to={`${basePath}/base`}
                    >
                      Model Base
                    </Nav.Link>
                  )}
                  {isTreeBasedModel && (
                    <Nav.Link
                      className="text-dark"
                      as={NavLink}
                      to={`${basePath}/trees`}
                    >
                      Trees
                    </Nav.Link>
                  )}
                  {(!isTreeBasedModel ||
                    (isAdvancedMode && isTreeBasedModel)) && (
                    <Nav.Link
                      className="text-dark"
                      as={NavLink}
                      to={`${basePath}/rules`}
                    >
                      Rules
                    </Nav.Link>
                  )}
                </>
              ) : (
                <Nav.Link
                  className="text-dark"
                  as={NavLink}
                  to={`${basePath}/event_weights`}
                >
                  Event Weights
                </Nav.Link>
              )}
              {isEngagementRevamp ? null : isMerged ? (
                <Nav.Link
                  className="text-dark"
                  as={NavLink}
                  to={`${basePath}/tuning`}
                >
                  Fine tuning
                </Nav.Link>
              ) : (
                <>
                  <Nav.Link
                    className="text-dark"
                    as={NavLink}
                    to={`${basePath}/thresholds`}
                  >
                    Thresholds
                  </Nav.Link>
                  {isCustomerFitModel || modelType === 'mqa' ? (
                    <Nav.Link
                      className="text-dark"
                      as={NavLink}
                      to={`${basePath}/overrides`}
                    >
                      Overrides
                    </Nav.Link>
                  ) : (
                    <Nav.Link
                      className="text-dark"
                      as={NavLink}
                      to={`${basePath}/lead_grade`}
                    >
                      Lead grade
                    </Nav.Link>
                  )}
                </>
              )}
              {/* The thresholds live on Event weights for an engagement model,
                  beside the weights that move the score they cut, so the tab
                  after it is the one thing left: the account model's
                  overrides, the lead models' grade. */}
              {isEngagementRevamp && (
                <Nav.Link
                  className="text-dark"
                  as={NavLink}
                  to={`${basePath}/${
                    modelType === 'mqa' ? 'overrides' : 'lead_grade'
                  }`}
                >
                  {modelType === 'mqa' ? 'Overrides' : 'Lead grade'}
                </Nav.Link>
              )}
              {/* Merged, Signals lives under Review with the sample that
                  renders it, so a second entry point here would be a third
                  place to look for one thing. */}
              {!isMerged && !isEngagementRevamp && (
                <Nav.Link
                  className="text-dark"
                  as={NavLink}
                  to={`${basePath}/signals`}
                >
                  Signals
                </Nav.Link>
              )}
              {activeModel.type === 'customer_fit' && (
                <Form className="ms-auto me-4 my-auto">
                  <Form.Switch
                    onChange={this.toggleAdvancedMode}
                    id="advanced-switch"
                    label="Advanced"
                    checked={this.state.isAdvancedMode}
                  />
                </Form>
              )}
            </Nav>
            <Switch>
              <Route path={`${path}/base`}>
                {isRevampEnabled ? <ModelBasePage /> : <ModelBaseChoicePage />}
              </Route>
              <Route path={`${path}/trees/:treeId/nodes/:nodeId/leads/:target`}>
                <NodeLeadsPage />
              </Route>
              <Route path={`${path}/trees/:treeId/nodes/:nodeId/insights`}>
                <NodeUnivariatePage />
              </Route>
              {isTreeBasedModel && (
                <Route path={`${path}/trees`}>
                  <TreesPage />
                </Route>
              )}
              <Route path={`${path}/rules`}>
                {/* Picked here rather than rewritten in place: flag off must
                    render exactly the page that shipped. */}
                {/* A tree model shows Rules only in advanced mode, so without it
                    this is a page the nav does not list. Reached by switching a rules
                    model to trees while on Rules. */}
                {isTreeBasedModel && !isAdvancedMode ? (
                  <Redirect to={`${basePath}/trees`} />
                ) : isTreeBasedModel ? (
                  <SmoothingPage />
                ) : isRevampEnabled ? (
                  <RulesPage />
                ) : (
                  <ScoringRulesPage />
                )}
              </Route>
              <Route path={`${path}/event_weights`}>
                {/* Picked here, not rewritten in place: flag off renders the
                    page that shipped, columns, buttons and all. */}
                {isEngagementRevamp ? <EventWeightsPage /> : <FeaturesPage />}
              </Route>
              {isMerged && (
                <Route path={`${path}/tuning`}>
                  <FineTuningPage />
                </Route>
              )}
              {isEngagementRevamp && (
                <Route path={`${path}/tuning`}>
                  <Redirect to={`${basePath}/event_weights`} />
                </Route>
              )}
              <Route path={`${path}/thresholds`}>
                {isEngagementRevamp ? (
                  <Redirect to={`${basePath}/event_weights`} />
                ) : isMerged ? (
                  <Redirect to={`${basePath}/tuning`} />
                ) : isCustomerFitModel ? (
                  <EnsemblingPage />
                ) : (
                  <BehavioralEnsemblingPage />
                )}
              </Route>
              <Route path={`${path}/overrides`}>
                {isEngagementRevamp ? (
                  <EngagementOverridesPage />
                ) : isMerged ? (
                  <Redirect to={`${basePath}/tuning`} />
                ) : (
                  <OverridesPage />
                )}
              </Route>
              <Route path={`${path}/lead_grade`}>
                {isEngagementRevamp ? (
                  <LeadGradeRevampPage />
                ) : (
                  <LeadGradePage />
                )}
              </Route>
              <Route path={`${path}/signals`}>
                {isMerged || isEngagementRevamp ? (
                  <Redirect
                    to={`/tenant/${tenant}/models/${modelId}/review/signals`}
                  />
                ) : isCustomerFitModel ? (
                  <SignalsPage />
                ) : (
                  <LtbSignalsPage />
                )}
              </Route>
              <Route path={path}>
                <Redirect to={getDefaultRoute()} />
              </Route>
            </Switch>
          </>
        );
      }
    }
  )
);

export default withRouter(ModelDetailsPage);
